import { Button } from "@showitinc/showit-wc";
import React, { useRef, useState } from "react";
import { showDomainSetupModal } from "../adaptors/ModalAdaptors";
import useUserDomains from "../hooks/query/useUserDomains";
import DomainListRow from "./domainmanager/DomainListRow";
import LoadingSpinner from "./LoadingSpinner";

/**
 * Lists the current user's enabled domains and lets the caller pick one
 * (e.g. to assign a site to a domain/subdomain).
 *
 * @param {Object} props
 * @param {number|null} [props.value] - Currently selected domain's site_id.
 * @param {(propInfo: { name: string, value: number|null, origValue: number|null }, event: Object) => void} [props.onChange]
 * @param {string} [props.propName] - Name reported back via onChange's propInfo.
 * @param {string} [props.height] - Height of the selector container.
 * @param {string} [props.defaultLabel] - Label for the "no domain" option.
 * @param {boolean} [props.showSetupOnEmptyList] - Show a "get started" CTA instead of an empty list.
 */
const DomainSelector = ({
  value,
  onChange,
  propName,
  height = "360px",
  defaultLabel = "No domain assigned",
  showSetupOnEmptyList = false,
}) => {
  const [selected, setSelected] = useState(value);
  // Written once at mount; `origValue` reports the pre-edit value on every change.
  const initValue = useRef(value).current;
  const { domains = [], isLoading, isError, isSuccess } = useUserDomains();

  const _selectDomain = (domain, e) => {
    const value = domain.site_id;

    setSelected(value);

    if (onChange) {
      const propInfo = _getPropInfo(value);
      onChange(propInfo, e);
    }
  };

  const _getPropInfo = (value) => ({
    name: propName,
    value,
    origValue: initValue,
  });

  const _getDomainList = () => {
    const enabledDomains = domains.filter((domain) => domain.disabled === 0);

    if (enabledDomains.length === 0 && showSetupOnEmptyList) {
      return (
        <div className={"view-centered-relative"} style={{ flexDirection: "column" }}>
          <div style={{ fontSize: 24, padding: 20 }}>Need to add a domain? No problem.</div>
          <Button variant="feature" onClick={showDomainSetupModal}>
            Click here to get started
          </Button>
        </div>
      );
    }

    const emptyOption = {
      domain_name: defaultLabel,
      site_id: null,
      design_name: null,
    };

    const domainList = [...enabledDomains, emptyOption];

    const listItems = domainList.map((domain, index) => (
      <DomainListRow
        key={index}
        domain={domain}
        selected={domain.site_id == selected}
        onClick={(event) => _selectDomain(domain, event)}
      />
    ));

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div className="si-table selectable">{listItems}</div>
      </div>
    );
  };

  const style = { height };

  let view = null;

  if (isLoading) {
    view = (
      <div className="view-centered-relative" style={{ flexDirection: "column" }}>
        <div
          style={{
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "rgba(255,255,255,0.3)",
            marginBottom: 10,
          }}
        >
          Loading
        </div>
        <LoadingSpinner />
      </div>
    );
  } else if (isError) {
    view = (
      <div className={"view-centered-relative"} style={{ flexDirection: "column" }}>
        <div>Error: Unable To Get Domain List</div>
      </div>
    );
  } else if (isSuccess) {
    view = _getDomainList();
  }

  return <div style={style}>{view}</div>;
};

export default DomainSelector;
